import React, { useState, useEffect } from 'react';
import './AdminReviews.css';
import StarRating from '../../StarRating';
import { useAuth } from '../../../contexts/AuthContext';
import { db, storage } from '../../../firebase';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';

function AdminReviews() {
    const { currentUser, isAdmin, loading: authLoading, login, logout } = useAuth();
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loginError, setLoginError] = useState('');
    const [loginLoading, setLoginLoading] = useState(false);
    const [editingDate, setEditingDate] = useState(null);

    const isAuthorized = currentUser && isAdmin && currentUser.email === 'info@offftheroast.com';

    useEffect(() => {
        if (!isAuthorized) {
            setLoading(false);
            return;
        }

        const q = query(collection(db, 'reviews'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setReviews(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
            setLoading(false);
        }, () => {
            setLoading(false);
        });
        return () => unsubscribe();
    }, [isAuthorized]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoginError('');
        setLoginLoading(true);
        try {
            await login(email, password);
        } catch (error) {
            setLoginError('Invalid email or password.');
        }
        setLoginLoading(false);
    };

    const toggleHidden = async (reviewId, currentHidden) => {
        await updateDoc(doc(db, 'reviews', reviewId), { hidden: !currentHidden });
    };

    const updateDate = async (reviewId, dateString) => {
        const newDate = new Date(dateString + 'T12:00:00');
        await updateDoc(doc(db, 'reviews', reviewId), { createdAt: Timestamp.fromDate(newDate) });
        setEditingDate(null);
    };

    const deleteReview = async (reviewId, photoPath) => {
        if (window.confirm('Permanently delete this review?')) {
            try {
                if (photoPath) {
                    const photoRef = ref(storage, photoPath);
                    await deleteObject(photoRef);
                }
                await deleteDoc(doc(db, 'reviews', reviewId));
            } catch (error) {
                console.error('Error deleting review:', error);
            }
        }
    };

    const filteredReviews = reviews.filter(r => {
        if (filter === 'visible') return !r.hidden;
        if (filter === 'hidden') return r.hidden;
        return true;
    });

    // Wait for auth to resolve before showing the login form
    if (authLoading) return null;

    // Not logged in - show login form
    if (!currentUser) {
        return (
            <div className='admin-container'>
                <h1 className='admin-reviews__title'>ADMIN</h1>
                <form className='admin-login' onSubmit={handleLogin}>
                    <h2>Sign In</h2>
                    {loginError && (
                        <div className='admin-login__error'>{loginError}</div>
                    )}
                    <div className='admin-login__group'>
                        <label>Email</label>
                        <input
                            type='email'
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                    </div>
                    <div className='admin-login__group'>
                        <label>Password</label>
                        <input
                            type='password'
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                        />
                    </div>
                    <button
                        type='submit'
                        className='admin-login__btn'
                        disabled={loginLoading}
                    >
                        {loginLoading ? 'SIGNING IN...' : 'SIGN IN'}
                    </button>
                </form>
            </div>
        );
    }

    // Logged in but not authorized
    if (!isAuthorized) {
        return (
            <div className='admin-container'>
                <h1 className='admin-reviews__title'>ADMIN</h1>
                <div className='admin-denied'>
                    <p>Access denied. You do not have admin privileges.</p>
                    <button className='admin-denied__btn' onClick={logout}>
                        SIGN OUT
                    </button>
                </div>
            </div>
        );
    }

    // Admin dashboard
    return (
        <div className='admin-container'>
            <h1 className='admin-reviews__title'>MANAGE REVIEWS</h1>
            <button className='admin-logout' onClick={logout}>
                SIGN OUT
            </button>

            <div className='admin-reviews__filters'>
                <button
                    className={filter === 'all' ? 'active' : ''}
                    onClick={() => setFilter('all')}
                >
                    All ({reviews.length})
                </button>
                <button
                    className={filter === 'visible' ? 'active' : ''}
                    onClick={() => setFilter('visible')}
                >
                    Visible ({reviews.filter(r => !r.hidden).length})
                </button>
                <button
                    className={filter === 'hidden' ? 'active' : ''}
                    onClick={() => setFilter('hidden')}
                >
                    Hidden ({reviews.filter(r => r.hidden).length})
                </button>
            </div>

            {loading ? (
                <p className='admin-reviews__empty'>Loading...</p>
            ) : filteredReviews.length === 0 ? (
                <p className='admin-reviews__empty'>No reviews found.</p>
            ) : (
                <div className='admin-reviews__list'>
                    {filteredReviews.map(review => (
                        <div
                            key={review.id}
                            className={`admin-review-card ${review.hidden ? 'admin-review-card--hidden' : ''}`}
                        >
                            <div className='admin-review-card__header'>
                                <span className='admin-review-card__name'>{review.name}</span>
                                <StarRating rating={review.rating} size='14px' />
                                {review.hidden && (
                                    <span className='admin-review-card__badge'>HIDDEN</span>
                                )}
                                {editingDate === review.id ? (
                                    <input
                                        type='date'
                                        className='admin-review-card__date-input'
                                        defaultValue={review.createdAt?.toDate().toISOString().split('T')[0]}
                                        onBlur={(e) => {
                                            if (e.target.value) {
                                                updateDate(review.id, e.target.value);
                                            } else {
                                                setEditingDate(null);
                                            }
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && e.target.value) {
                                                updateDate(review.id, e.target.value);
                                            } else if (e.key === 'Escape') {
                                                setEditingDate(null);
                                            }
                                        }}
                                        autoFocus
                                    />
                                ) : (
                                    <span
                                        className='admin-review-card__date admin-review-card__date--editable'
                                        onClick={() => setEditingDate(review.id)}
                                        title='Click to edit date'
                                    >
                                        {review.createdAt?.toDate().toLocaleDateString()}
                                    </span>
                                )}
                            </div>
                            <p className='admin-review-card__text'>{review.text}</p>
                            {review.photoURL && (
                                <img
                                    src={review.photoURL}
                                    alt='Review'
                                    className='admin-review-card__photo'
                                />
                            )}
                            <div className='admin-review-card__actions'>
                                <button
                                    className='admin-review-card__hide'
                                    onClick={() => toggleHidden(review.id, review.hidden)}
                                >
                                    {review.hidden ? 'SHOW' : 'HIDE'}
                                </button>
                                <button
                                    className='admin-review-card__delete'
                                    onClick={() => deleteReview(review.id, review.photoPath)}
                                >
                                    DELETE
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default AdminReviews;
